import { DEFAULT_ALL_VALUE } from '@chronosphereio/core';
import { Draft, produce } from 'immer';
import { isEqual } from 'lodash';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { createStore, useStore } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { shallow } from 'zustand/shallow';
// useStore with three params was deprecated in favor of useStoreWithEqualityFn.
// See: https://github.com/pmndrs/zustand/discussions/1937
import { useStoreWithEqualityFn } from 'zustand/traditional';
import {
  isListVariableDefinition,
  ListVariableDefinition,
  SavedView,
  SavedViewsDefinition,
  VariableDefinition,
  VariableName,
  VariableOption,
  VariableStateMap,
  VariableValue,
} from '@/core';
import { TemplateVariableContextProvider, useDependsOnVariables, useListVariablePluginValues } from '@/plugin-system';
import { getInitalValuesFromQueryParameters, getURLQueryParamName, useVariableQueryParams } from './query-params';
import {
  checkSavedDefaultVariableStatus,
  ensureOnlyAllValueIsSelected,
  hydrateInitialVariableStates,
  isAllValueSelected,
  processVariableAllState,
  resolveRuntimeDefault,
} from './utils';

type TemplateVariableStore = {
  variableDefinitions: VariableDefinition[];
  variableState: VariableStateMap;
  savedViewsDefinition?: SavedViewsDefinition;
  setVariableValue: (variableName: VariableName, value: VariableValue) => void;
  setVariableOptions: (name: VariableName, options: VariableOption[], dependsOnVariableValuesKey: string) => void;
  setVariableLoading: (name: VariableName, loading: boolean) => void;
  setVariableDefinitions: (definitions: VariableDefinition[]) => void;
  setVariableDefaultValues: () => VariableDefinition[];
  getSavedVariablesStatus: () => { isSavedVariableModified: boolean; modifiedVariableNames: string[] };
  setSavedViewsDefinition: (savedViews: SavedViewsDefinition | undefined) => void;
  setVariableStateFromSavedView: (view: SavedView) => void;
};

const TemplateVariableStoreContext = createContext<ReturnType<typeof createTemplateVariableSrvStore> | undefined>(
  undefined
);
// Fallback store for useOptionalTemplateVariableDefinitions when no provider is in the tree.
const emptyTemplateVariableStore = createTemplateVariableSrvStore({});
function useTemplateVariableStoreCtx() {
  const context = useContext(TemplateVariableStoreContext);
  if (!context) {
    throw new Error('TemplateVariableStoreContext not initialized');
  }
  return context;
}

export function useDashboardTemplateVariableValues(variableNames?: string[]) {
  const store = useTemplateVariableStoreCtx();
  const state = useStoreWithEqualityFn(
    store,
    (s) => {
      const names = variableNames ?? Object.keys(s.variableState);
      const vars: VariableStateMap = {};
      names.forEach((name) => {
        const varState = s.variableState[name];
        if (!varState) {
          return;
        }
        vars[name] = varState;
      });
      return vars;
    },
    shallow
  );
  return state;
}

export function useProcessVariableAllState() {
  const originalValues = useDashboardTemplateVariableValues();
  const definitions = useTemplateVariableDefinitions();
  const processedVariableState = useMemo(
    () => processVariableAllState(definitions, originalValues),
    [definitions, originalValues]
  );
  return processedVariableState;
}

export function useTemplateVariable(name: string) {
  const store = useTemplateVariableStoreCtx();
  return useStore(store, (s) => {
    const variableState = s.variableState[name];

    const definition = s.variableDefinitions.find((v) => v.spec.name === name);
    return {
      state: variableState,
      definition,
    };
  });
}

export function useTemplateVariableActions() {
  const store = useTemplateVariableStoreCtx();
  const actions = useStoreWithEqualityFn(
    store,
    (s) => ({
      setVariableValue: s.setVariableValue,
      setVariableLoading: s.setVariableLoading,
      setVariableOptions: s.setVariableOptions,
      setVariableDefinitions: s.setVariableDefinitions,
      setVariableDefaultValues: s.setVariableDefaultValues,
      getSavedVariablesStatus: s.getSavedVariablesStatus,
      setSavedViewsDefinition: s.setSavedViewsDefinition,
      setVariableStateFromSavedView: s.setVariableStateFromSavedView,
    }),
    shallow
  );
  return actions;
}

export function useTemplateVariableDefinitions() {
  const store = useTemplateVariableStoreCtx();
  return useStore(store, (s) => s.variableDefinitions);
}

export function useOptionalTemplateVariableDefinitions() {
  const store = useContext(TemplateVariableStoreContext);
  return useStore(store ?? emptyTemplateVariableStore, (s) => s.variableDefinitions);
}

export function useSavedViewsDefinition() {
  const store = useTemplateVariableStoreCtx();
  return useStore(store, (s) => s.savedViewsDefinition);
}

export function useSavedViews(): SavedView[] {
  const store = useTemplateVariableStoreCtx();
  return useStore(store, (s) => {
    const savedViewsDefinition = s.savedViewsDefinition;
    if (!savedViewsDefinition) {
      return [];
    }
    return savedViewsDefinition.spec.views.map((view) => view.spec);
  });
}

export function useDefaultViewName(): string | undefined {
  const store = useTemplateVariableStoreCtx();
  return useStore(store, (s) => s.savedViewsDefinition?.spec.default_view_name);
}

export function useTemplateVariableStore() {
  const store = useTemplateVariableStoreCtx();
  return useStore(store);
}

// Returns the value of all of the variables in the store
// If a variable is in it's default state, it will be undefined
// The result of this should be used to match a saved view
export function useTemplateVariableViewState() {
  const store = useTemplateVariableStoreCtx();
  return useStoreWithEqualityFn(
    store,
    (s) => {
      const state = { ...s.variableState };
      const definitions = s.variableDefinitions;

      // We run through this hydrate function with empty initial values to get all of the default values
      const defaultView = Object.entries(hydrateInitialVariableStates(definitions, {})).reduce(
        (acc, [name, variable]) => {
          let value = variable.value;

          // When the hydrated default is null (no explicit default_value in spec),
          // resolve using loaded options to match the runtime default the user sees.
          if (value === null) {
            const runtimeState = state[name];
            const variableDef = definitions.find((d) => d.spec.name === name);
            if (variableDef && runtimeState?.options) {
              value = resolveRuntimeDefault(variableDef, runtimeState.options);
            }
          }

          acc[name] = value;
          return acc;
        },
        {} as Record<string, VariableValue>
      );

      // Put all of the current values into the view
      // If it's the default value, don't include it.  This  will be the current view
      const currentView = Object.entries(state).reduce(
        (acc, [name, variable]) => {
          acc[name] = variable.value;
          return acc;
        },
        {} as Record<string, VariableValue>
      );

      // Consider variables loading if any variable is actively fetching OR if
      // any ListVariable hasn't completed its initial option fetch yet. The
      // latter covers gaps between dependent variable loads where no variable
      // has loading=true but a re-query is about to start. We also check that
      // options are empty to avoid a false positive after saved view selection,
      // where hasLoaded is reset but options are still populated from a
      // previous load.
      const isLoading = definitions.some((def) => {
        const varState = state[def.spec.name];
        if (!varState) return false;
        if (varState.loading) return true;
        return (
          isListVariableDefinition(def) && !varState.hasLoaded && (!varState.options || varState.options.length === 0)
        );
      });

      return { currentView, defaultView, isLoading };
    },
    shallow
  );
}

function PluginProvider({ children }: { children: React.ReactNode }) {
  const values = useProcessVariableAllState();
  return (
    <TemplateVariableContextProvider value={{ state: values }}>
      <VariableDataLoader />
      {children}
    </TemplateVariableContextProvider>
  );
}

interface TemplateVariableSrvArgs {
  initialVariableDefinitions?: VariableDefinition[];
  queryParams?: ReturnType<typeof useVariableQueryParams>;
  initialValues?: Record<string, VariableValue>;
  /**
   * List of variable names that should be ignored when `setVariableValue` is called.
   */
  ignoredVariables?: string[];
  savedViewsDefinition?: SavedViewsDefinition;
  namespace?: string;
}

function createTemplateVariableSrvStore({
  initialVariableDefinitions = [],
  queryParams,
  initialValues,
  ignoredVariables,
  savedViewsDefinition,
  namespace,
}: TemplateVariableSrvArgs) {
  const initialParams = {
    ...initialValues,
    ...getInitalValuesFromQueryParameters(queryParams ? queryParams[0] : {}, namespace),
  };
  const store = createStore<TemplateVariableStore>()(
    devtools(
      immer((set, get) => {
        const setVariableValue = (name: string, value: VariableValue, state: Draft<TemplateVariableStore>) => {
          let val = value;
          const varState = state.variableState[name];
          if (!varState) {
            return;
          }

          const variableDef = state.variableDefinitions.find((v) => v.spec.name === name);

          // Make sure there is only one all value
          if (Array.isArray(val)) {
            val = ensureOnlyAllValueIsSelected(val);

            // We always convert single item arrays to the item itself, e.g. val = val[0]
            if (val.length === 1) {
              val = val[0]!;
            }

            if (isAllValueSelected(val)) {
              // All value is special and should never be an array
              val = DEFAULT_ALL_VALUE;
            }
          }

          const queryParamName = getURLQueryParamName(name, namespace);
          if (queryParams) {
            if (!ignoredVariables?.includes(name)) {
              const setQueryParams = queryParams[1];
              setQueryParams({ [queryParamName]: val });
            }
          }

          // If the variable is a list variable and allow_multiple is set to true, we always store the value as an array
          if (variableDef?.kind === 'ListVariable' && variableDef?.spec.allow_multiple && typeof val === 'string') {
            varState.value = [val];
          } else {
            varState.value = val;
          }
        };

        return {
          variableState: hydrateInitialVariableStates(initialVariableDefinitions, initialParams),
          variableDefinitions: initialVariableDefinitions,
          setVariableDefinitions(definitions: VariableDefinition[]) {
            set(
              (state) => {
                state.variableDefinitions = definitions;
                const newState = hydrateInitialVariableStates(definitions, initialParams);
                // Go through all the state variables and add new ones if they don't exist
                state.variableState = { ...newState, ...state.variableState };
              },
              false,
              '[Variables] setVariableDefinitions' // Used for action name in Redux devtools
            );
          },
          setVariableOptions(name, options, dependsOnVariableValuesKey) {
            set(
              (state) => {
                const varState = state.variableState[name];
                if (!varState) {
                  return;
                }
                varState.options = options;

                // Skip resetting the value if the options have already been loaded and the dependsOnVariableValuesKey is the same
                const skipReset = dependsOnVariableValuesKey === varState.lastDependsOnVariableValuesKey;
                if (varState.hasLoaded && skipReset === false) {
                  // If the selected value doesn't appear to be in the new options, reset the value
                  if (varState.value)
                    if (Array.isArray(varState.value)) {
                      const everyValueInOptions = varState.value.every((value) => {
                        return options.find((o) => o.value === value) !== undefined;
                      });

                      if (!everyValueInOptions) {
                        varState.value = null;
                      }
                    } else if (!options.find((o) => o.value === varState.value)) {
                      varState.value = null;
                    }
                } else {
                  varState.hasLoaded = true;
                }

                // If value is null and we have options, set a default value.
                // Important for cases when variable dropdown is hidden in responsive menu.
                const variableDef = state.variableDefinitions.find((v) => v.spec.name === name);
                if (
                  varState.value === null &&
                  options.length > 0 &&
                  variableDef &&
                  isListVariableDefinition(variableDef)
                ) {
                  let defaultValueToSet: VariableValue | null = null;

                  // Check if default_value is in the options
                  if (variableDef.spec.default_value) {
                    const defaultValue = variableDef.spec.default_value;
                    const defaultInOptions = Array.isArray(defaultValue)
                      ? defaultValue.every((value) => options.find((option) => option.value === value))
                      : options.find((option) => option.value === defaultValue);
                    if (defaultInOptions) {
                      defaultValueToSet = defaultValue;
                    }
                  }

                  // If still no value, use first option or all value if allow_all_value
                  if (defaultValueToSet === null) {
                    if (variableDef.spec.allow_all_value) {
                      defaultValueToSet = DEFAULT_ALL_VALUE;
                    } else {
                      const firstOption = options[0];
                      if (firstOption) {
                        defaultValueToSet = variableDef.spec.allow_multiple ? [firstOption.value] : firstOption.value;
                      }
                    }
                  }

                  // Call setVariableValue to ensure query params are updated
                  if (defaultValueToSet !== null) {
                    setVariableValue(name, defaultValueToSet, state);
                  }
                }

                varState.lastDependsOnVariableValuesKey = dependsOnVariableValuesKey;
              },
              false,
              '[Variables] setVariableOptions'
            );
          },
          setVariableLoading(name, loading) {
            set(
              (state) => {
                const varState = state.variableState[name];
                if (!varState) {
                  return;
                }

                varState.loading = loading;
              },
              false,
              '[Variables] setVariableLoading'
            );
          },
          setVariableValue: (name, value) =>
            set(
              (state) => {
                setVariableValue(name, value, state);
              },
              false,
              '[Variables] setVariableValue'
            ),
          setVariableDefaultValues: () => {
            const variableDefinitions = get().variableDefinitions;
            const variableState = get().variableState;
            const updatedVariables = produce(variableDefinitions, (draft) => {
              draft.forEach((variable) => {
                const currentVariable = variableState[variable.spec.name];
                if (currentVariable?.value == null) return;
                if (variable.kind === 'ListVariable') {
                  const newValue =
                    Array.isArray(currentVariable.value) && currentVariable.value.length === 1
                      ? currentVariable.value[0]
                      : currentVariable.value;
                  variable.spec.default_value = newValue;
                } else if (variable.kind === 'IntervalVariable') {
                  variable.spec.default_value = Array.isArray(currentVariable.value)
                    ? currentVariable.value[0]
                    : currentVariable.value;
                } else if (variable.kind === 'TextVariable') {
                  variable.spec.value = typeof currentVariable.value === 'string' ? currentVariable.value : '';
                }
              });
            });
            set(
              (state) => {
                state.variableDefinitions = updatedVariables;
              },
              false,
              '[Variables] setVariableDefaultValues'
            );
            return updatedVariables;
          },
          getSavedVariablesStatus: () => {
            return checkSavedDefaultVariableStatus(get().variableDefinitions, get().variableState);
          },
          savedViewsDefinition,
          setSavedViewsDefinition: (views: SavedViewsDefinition | undefined) => {
            set(
              (state) => {
                state.savedViewsDefinition = views;
              },
              false,
              '[Variables] setSavedViewsDefinition' // Used for action name in Redux devtools
            );
          },
          setVariableStateFromSavedView: (savedView: SavedView) =>
            set(
              (state) => {
                Object.entries(savedView.variable_values).forEach(([name, value]) => {
                  setVariableValue(name, value, state);
                  const varState = state.variableState[name];
                  if (varState !== undefined) {
                    varState.hasLoaded = false;
                  }
                });
              },
              false,
              '[Variables] setVariableFromPreset'
            ),
        };
      }),
      { name: 'TemplateVariableStore' }
    )
  );

  return store;
}

export type ComplexUrlParams = {
  enabled?: boolean;
  ignoredVariables?: string[];
  namespace?: string;
};
export interface TemplateVariableProviderProps {
  children: React.ReactNode;
  initialVariableDefinitions?: VariableDefinition[];
  urlParams?: boolean | ComplexUrlParams;
  initialValues?: Record<string, VariableValue>;
  savedViewsDefinition?: SavedViewsDefinition;
}

export function TemplateVariableProvider({
  children,
  initialVariableDefinitions = [],
  urlParams,
  initialValues,
  savedViewsDefinition,
}: TemplateVariableProviderProps) {
  // Set URL params enabled to
  // 1. the boolean value of the 'urlParams' prop
  // 2. the boolean value of the nested 'enabled' prop
  // 3. a default value of true if no value is provided
  const urlParamsEnabled = typeof urlParams === 'boolean' ? urlParams : (urlParams?.enabled ?? true);
  if (urlParamsEnabled) {
    return TemplateVariableProviderWithQueryParams({
      children,
      initialVariableDefinitions,
      urlParams: typeof urlParams === 'object' ? urlParams : {},
      savedViewsDefinition,
    });
  }

  return TemplateVariableProviderWithoutQueryParams({
    children,
    initialVariableDefinitions,
    initialValues,
    savedViewsDefinition,
  });
}

/**
 * Ensures the store values are in sync with the query params
 * This can happen if the URL params are changed directly in the browser, such as using the back button
 * when the time range was changed.
 */
function useSyncQueryParamsWithVariableValues(args: {
  initialVariableDefinitions: VariableDefinition[];
  queryParamsState: ReturnType<typeof useVariableQueryParams>;
  store: ReturnType<typeof createTemplateVariableSrvStore>;
  namespace?: string;
}) {
  const { initialVariableDefinitions, queryParamsState, store, namespace } = args;
  const [queryParams] = queryParamsState;

  useEffect(() => {
    const originalState = hydrateInitialVariableStates(initialVariableDefinitions, {});

    // For each variable in the store, check if the value in the store is different from the URL param value
    Object.entries(store.getState().variableState).forEach(([name, variable]) => {
      const queryParamName = getURLQueryParamName(name, namespace);
      const queryParamValue = queryParams[queryParamName];
      const defaultVariableValue = originalState[name]?.value;

      // If the value in the store is different from the URL param value
      if (!isEqual(variable.value, queryParamValue)) {
        // If the variable is not set in the URL params and the value in the store is the same as the default value,
        // we can skip setting the valuje since it's already in sync
        if (queryParamValue === '' && isEqual(variable.value, defaultVariableValue)) {
          return;
        }
        // reset the value in the store to resync the URL param value
        store.getState().setVariableValue(name, variable.value);
      }
    });
  }, [initialVariableDefinitions, queryParams, store, namespace]);
}

function TemplateVariableProviderWithQueryParams({
  children,
  initialVariableDefinitions = [],
  urlParams,
  savedViewsDefinition,
}: TemplateVariableProviderProps & { urlParams: ComplexUrlParams }) {
  const queryParams = useVariableQueryParams(
    initialVariableDefinitions.filter((d) => !urlParams?.ignoredVariables?.includes(d.spec.name)),
    urlParams?.namespace
  );

  const [store] = useState(
    createTemplateVariableSrvStore({
      initialVariableDefinitions,
      queryParams,
      ignoredVariables: urlParams?.ignoredVariables,
      savedViewsDefinition,
      namespace: urlParams?.namespace,
    })
  );

  useSyncQueryParamsWithVariableValues({
    initialVariableDefinitions,
    queryParamsState: queryParams,
    store,
    namespace: urlParams?.namespace,
  });

  return (
    <TemplateVariableStoreContext.Provider value={store}>
      <PluginProvider>{children}</PluginProvider>
    </TemplateVariableStoreContext.Provider>
  );
}

function TemplateVariableProviderWithoutQueryParams({
  children,
  initialVariableDefinitions = [],
  initialValues,
  savedViewsDefinition,
}: TemplateVariableProviderProps) {
  const [store] = useState(
    createTemplateVariableSrvStore({ initialVariableDefinitions, initialValues, savedViewsDefinition })
  );

  return (
    <TemplateVariableStoreContext.Provider value={store}>
      <PluginProvider>{children}</PluginProvider>
    </TemplateVariableStoreContext.Provider>
  );
}

/**
 * Variable loader to decouple data loading from UI rendering.
 */
function VariableDataLoader() {
  const variableDefinitions = useTemplateVariableDefinitions();
  const listVariables = variableDefinitions.filter(isListVariableDefinition);
  return (
    <>
      {listVariables.map((definition) => (
        <ListVariableDataLoader key={definition.spec.name} definition={definition} />
      ))}
    </>
  );
}

function ListVariableDataLoader({ definition }: { definition: ListVariableDefinition }) {
  const variablesOptionsQuery = useListVariablePluginValues(definition);
  const { isFetching, data } = variablesOptionsQuery;
  const { setVariableLoading, setVariableOptions } = useTemplateVariableActions();

  const dependsOnData = useDependsOnVariables(definition);
  const dependsOnVariableValuesKey = dependsOnData.variablesValueKey;

  const name = definition.spec.name;

  useEffect(() => {
    setVariableLoading(name, isFetching);
    if (data) {
      setVariableOptions(name, data, dependsOnVariableValuesKey);
    }
    // Do not include dependsOnVariableValuesKey in dep array to avoid race condition
    // that would result in setVariableOptions being called with stale data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetching, data, name, setVariableLoading, setVariableOptions]);

  // Do not render anything, only responsible for variable data loading.
  return null;
}
